/**
 * Manage ▸ **Run history** (captures `04`, `60-run-history-populated.png`,
 * `61-run-history-expanded.png`).
 *
 * A row is `status · sentence · timestamp · ⌄`. Expanded it shows the numbered steps
 * (1 trigger, 2 condition, 3 action), the `Item` chip, `Triggered by: Automation`, and a
 * footer with *Report issue*, *View automation*, the step count and the duration. A run whose
 * automation has been deleted still lists — the history outlives the definition (§5.1).
 */
import { Icon, formatMondayDateTime, icons } from '@elizmonday/ui';
import { useState } from 'react';
import { automationBlockRegistry } from '@elizmonday/shared';
import type { RunRowView, RunStepView } from './types';

const STATUS_TEXT: Readonly<Record<string, string>> = {
  succeeded: 'Success',
  failed: 'Failed',
  running: 'Running',
  waiting: 'Waiting',
  skipped_condition: "Didn't run",
  skipped_loop: "Didn't run",
  skipped_permission: "Didn't run",
  skipped_disabled: "Didn't run",
  cancelled: 'Cancelled',
};

export function runStatusText(status: string): string {
  return STATUS_TEXT[status] ?? status;
}

/** `Sep 10, 2026 5:54 PM` — the format the capture shows. */
export function formatRunTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return formatMondayDateTime(date);
}

export function formatDuration(ms: number | null): string {
  if (ms === null) return '—';
  if (ms < 1_000) return `${String(ms)}ms`;
  return `${String(Math.round(ms / 100) / 10)}s`;
}

export interface RunHistoryProps {
  runs: RunRowView[];
  /** Loads the steps of one run the first time it is expanded. */
  loadSteps(runId: string): Promise<RunStepView[]>;
  onViewAutomation(automationId: string): void;
  includeSkipped: boolean;
  onIncludeSkippedChange(next: boolean): void;
}

export function RunHistory({ runs, loadSteps, onViewAutomation, includeSkipped, onIncludeSkippedChange }: RunHistoryProps) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [steps, setSteps] = useState<Record<string, RunStepView[]>>({});

  const expand = async (runId: string): Promise<void> => {
    if (expanded === runId) {
      setExpanded(null);
      return;
    }
    setExpanded(runId);
    if (steps[runId] === undefined) {
      const loaded = await loadSteps(runId);
      setSteps((current) => ({ ...current, [runId]: loaded }));
    }
  };

  return (
    <div className="ez-automation-manage__panel" data-testid="run-history-panel">
      <div className="ez-automation-toolbar">
        <button
          type="button"
          className="ez-automation-toolbar__filter"
          aria-pressed={includeSkipped}
          data-testid="run-history-filters"
          onClick={() => onIncludeSkippedChange(!includeSkipped)}
        >
          <Icon icon={icons.filter} size={16} ignoreFocusStyle />
          Filters
        </button>
      </div>

      {runs.length === 0 ? (
        <div className="ez-automation-empty" data-testid="run-history-empty">
          <p className="ez-automation-empty__title">No automation has run on this board yet</p>
        </div>
      ) : (
        <ul className="ez-automation-runs" data-testid="run-history-rows">
          {runs.map((run) => (
            <li key={run.id} className="ez-automation-run" data-expanded={expanded === run.id} data-testid={`run-row-${run.id}`}>
              <button
                type="button"
                className="ez-automation-run__head"
                aria-expanded={expanded === run.id}
                data-testid={`run-toggle-${run.id}`}
                onClick={() => void expand(run.id)}
              >
                <span className="ez-automation-run__status" data-status={run.status}>
                  <Icon icon={run.status === 'succeeded' ? icons.checkSmall : icons.close} size={16} ignoreFocusStyle />
                  {runStatusText(run.status)}
                </span>
                <span className="ez-automation-run__sentence">
                  {run.automationDeleted ? 'This automation was deleted' : run.automationTitle}
                </span>
                <span className="ez-automation-run__time">{formatRunTime(run.startedAt)}</span>
                <Icon icon={expanded === run.id ? icons.disclosureOpen : icons.chevronDown} size={16} ignoreFocusStyle />
              </button>

              {expanded === run.id ? (
                <div className="ez-automation-run__body" data-testid={`run-body-${run.id}`}>
                  {(steps[run.id] ?? []).map((step, index) => (
                    <div key={step.seq} className="ez-automation-run__step">
                      <span className="ez-automation-run__step-no">{index + 1}</span>
                      <div className="ez-automation-run__step-card">
                        <p className="ez-automation-run__step-sentence">{sentenceOf(step.block)}</p>
                        {index !== 0 ? null : (
                          <>
                            {run.itemId === null ? null : (
                              <p className="ez-automation-run__step-meta">
                                <Icon icon={icons.item} size={14} ignoreFocusStyle />
                                Item <span className="ez-automation-run__chip">{run.itemName ?? run.itemId}</span>
                              </p>
                            )}
                            <p className="ez-automation-run__step-meta">
                              <Icon icon={icons.automation} size={14} ignoreFocusStyle />
                              Triggered by: {run.triggeredBy === 'automation' ? 'Automation' : 'User'}
                            </p>
                          </>
                        )}
                        {step.status === 'failed' ? (
                          <p className="ez-automation-run__step-error">{errorText(step.error)}</p>
                        ) : null}
                      </div>
                    </div>
                  ))}

                  <div className="ez-automation-run__footer">
                    <span className="ez-automation-run__footer-item">
                      <Icon icon={icons.openInNewTab} size={14} ignoreFocusStyle />
                      Report issue
                    </span>
                    <button
                      type="button"
                      className="ez-automation-link"
                      disabled={run.automationDeleted}
                      data-testid={`run-view-automation-${run.id}`}
                      onClick={() => onViewAutomation(run.automationId)}
                    >
                      View automation
                    </button>
                    <span className="ez-automation-run__footer-item">
                      <Icon icon={icons.settings} size={14} ignoreFocusStyle />
                      {(steps[run.id] ?? []).length}
                    </span>
                    <span className="ez-automation-run__footer-item" data-testid={`run-duration-${run.id}`}>
                      <Icon icon={icons.activity} size={14} ignoreFocusStyle />
                      {formatDuration(run.durationMs)}
                    </span>
                  </div>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function sentenceOf(blockId: string): string {
  const block = automationBlockRegistry.find(blockId);
  if (block === undefined) return blockId;
  return block.sentencePartial.replace(/\{([^{},]*),\s*[A-Za-z0-9_]+\}/g, '$1');
}

function errorText(error: unknown): string {
  if (error === null || error === undefined) return 'Failed';
  if (typeof error === 'string') return error;
  const record = error as { message?: unknown; code?: unknown };
  return String(record.message ?? record.code ?? 'Failed');
}
